import type { IStudio } from "../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

/**
 * Canal de sincronización control window ↔ OSR window (mismo origen + misma partition →
 * BroadcastChannel los conecta). El control (studio) publica el modelo de escena y los
 * comandos start/stop; el OSR (engine) los aplica sobre Radio.
 */

const CHANNEL = "eriol-broadcast";

/** Salud del broadcast que el OSR reporta al control cada 1s (la muestra el title bar). */

export interface BroadcastConnectionStats {
  /** Encoder de video del OSR (NVENC / Media Foundation / VideoToolbox). null hasta la
   * primera muestra. */
  encoder: {
    kind: string;

    fps: number;

    bitrateKbps: number;

    frames: number;

    droppedFrames: number;
  } | null;

  /** Salidas RTMP del broadcast local (una por destino), según kero. */
  outputs: {
    name: string;

    outKbps: number;

    unackedBytes: number;

    queueMs: number;
  }[];

  /** Salud informada por kero vía WebTransport (cloud/selfhost); null en local. */
  transport: {
    avgBitrateKbps: number;

    droppedFrames: number;

    rttMs: number;
  } | null;
}

/** Códigos de aviso NO-fatal. El control los traduce vía i18n
 * (`dashboard:DASHBOARD.broadcastWarnings.<code>`) para no forzar un idioma. */
export type BroadcastWarningCode =
  | "cloudDestinationsFailed"
  | "cloudRecordingFailed";

/** Duraciones (ms) de cada etapa del arranque del OSR, medidas desde la carga de su página. */
export interface BroadcastStartTimeline {
  /** Carga de la app hasta montar la composición. */
  loadMs: number;
  /** Radio.initialize() + pedido de escena al control hasta recibirla. */
  sceneMs: number;
  /** Montaje de fuentes (cámaras, browser, etc.). */
  sourcesMs: number;
  /** startScene (Pixi/WebGPU + assets de la escena). */
  stageMs: number;
  /** Radio.start(): config entregado a kero. */
  startMs: number;
  totalMs: number;
  /** Veces que hubo que repetir `request-scene` hasta que el control respondió. */
  sceneRetries: number;
  /** Marcas `eriol-start:*` (StrategyDraw/PixiFactory) en ms desde el montaje: desglose del stage. */
  marks: Record<string, number>;
}

export type BroadcastMessage =
  | { type: "scene"; model: IStudio }
  | { type: "request-scene" }
  | { type: "control"; action: "start" | "stop" }

  // OSR → control: el arranque del broadcast falló (ej. concurrent_limit_reached).

  // `raw` es el message del error (puede ser "HTTP 400: <json>") para que el control

  // lo parsee y muestre el toast adecuado.
  | { type: "broadcast-error"; raw: string }
  // OSR → control: el broadcast arrancó de verdad (config entregado a kero). Hasta este
  // mensaje el control sigue en "Iniciando…" (antes pasaba a "en vivo" apenas se creaba la
  // ventana OSR, y el usuario esperaba sin feedback). `timeline` va al log para soporte.
  | { type: "broadcast-started"; timeline: BroadcastStartTimeline }

  // OSR → control: aviso NO-fatal. El broadcast local sigue al aire pero algo

  // secundario se degradó (ej. la parte cloud falló por falta de plan). El control

  // traduce `code` vía i18n y muestra un toast informativo (NO resetea onLive).
  | { type: "broadcast-warning"; code: BroadcastWarningCode }

  // OSR → control: salud del broadcast periódica (la consume `initStatsListener` en

  // controlBroadcast → useBroadcastStats → BroadcastHealth en el title bar).
  | {
      type: "stats";

      action: "connectionUpdate";

      payload: BroadcastConnectionStats;
    };

export class SceneSyncChannel {
  private bc: BroadcastChannel;

  constructor() {
    this.bc = new BroadcastChannel(CHANNEL);
  }

  post(msg: BroadcastMessage): void {
    this.bc.postMessage(msg);
  }

  /** Registra un handler; devuelve la función para desuscribir. */

  on(handler: (msg: BroadcastMessage) => void): () => void {
    const listener = (e: MessageEvent) => handler(e.data as BroadcastMessage);

    this.bc.addEventListener("message", listener);

    return () => this.bc.removeEventListener("message", listener);
  }

  close(): void {
    this.bc.close();
  }
}
